import React from 'react';
import { MapPin, Clock, Briefcase, Wallet, Hash, Users } from 'lucide-react';
import {
  JobPositionDetail, CONTRACT_LABELS, SENIORITY_LABELS, DEPARTMENT_LABELS,
  REMOTE_LABELS, formatRetribuzione,
} from '../../services/careersService';

const Badge = ({ children }: { children: React.ReactNode }) => (
  <span className="text-xs px-2 py-0.5 rounded-md border bg-secondary/60">{children}</span>
);

const BulletList = ({ title, items, testId }: { title: string; items: string[]; testId: string }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-8" data-testid={testId}>
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2.5 text-base leading-relaxed text-muted-foreground">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[hsl(214,58%,41%)] flex-shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Rendering del contenuto di una posizione — condiviso tra pagina pubblica
 * di dettaglio e anteprima admin (nessuna duplicazione di markup).
 * I blocchi opzionali vuoti non vengono renderizzati. */
export function JobPositionView({ pos, showCta = true, onApply }: {
  pos: JobPositionDetail;
  showCta?: boolean;
  onApply?: () => void;
}) {
  const retribuzione = formatRetribuzione(pos.retribuzione);
  const mailSubject = `Candidatura: ${pos.titolo}${pos.riferimento ? ` [rif. ${pos.riferimento}]` : ''}`;
  const mailBody = `Buongiorno,\n\nvorrei candidarmi per la posizione "${pos.titolo}"${pos.riferimento ? ` (rif. ${pos.riferimento})` : ''}.\nIn allegato trovate il mio curriculum vitae, con autorizzazione al trattamento dei dati personali ai sensi del Reg. UE 2016/679.\n\nCordiali saluti,\n`;
  const mailto = `mailto:${pos.emailCandidature}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`;

  return (
    <div data-testid="job-position-view">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3" data-testid="job-title">{pos.titolo}</h1>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Badge>{CONTRACT_LABELS[pos.tipoContratto]}</Badge>
          <Badge>{DEPARTMENT_LABELS[pos.area]}</Badge>
          <Badge>{SENIORITY_LABELS[pos.seniority]}</Badge>
          {pos.sedeRemoto !== 'in_sede' && <Badge>{REMOTE_LABELS[pos.sedeRemoto]}</Badge>}
        </div>
        <div className="flex items-center gap-x-5 gap-y-1.5 flex-wrap text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {pos.sede}</span>
          {pos.dataPubblicazione && (
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Pubblicata il {new Date(pos.dataPubblicazione).toLocaleDateString('it-IT')}</span>
          )}
          {pos.riferimento && (
            <span className="flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" /> Rif. {pos.riferimento}</span>
          )}
          {pos.posizioniDisponibili > 1 && (
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {pos.posizioniDisponibili} posizioni</span>
          )}
          {retribuzione && (
            <span className="flex items-center gap-1.5 font-medium text-foreground" data-testid="job-retribuzione">
              <Wallet className="w-3.5 h-3.5" /> {retribuzione}
            </span>
          )}
        </div>
      </div>

      {pos.descrizione && (
        <div className="mt-8" data-testid="job-descrizione">
          <h2 className="text-lg font-semibold mb-3">Il ruolo</h2>
          <div
            className="job-richtext text-base leading-relaxed text-muted-foreground space-y-3"
            dangerouslySetInnerHTML={{ __html: pos.descrizione }}
          />
        </div>
      )}

      <BulletList title="Requisiti" items={pos.requisiti} testId="job-requisiti" />
      <BulletList title="Requisiti preferenziali" items={pos.requisitiPreferenziali} testId="job-requisiti-pref" />
      <BulletList title="Cosa offriamo" items={pos.offriamo} testId="job-offriamo" />

      {showCta && (
        <div className="rounded-xl border bg-[hsl(var(--dtk-canvas))] p-6 mt-8" data-testid="job-cta-box">
          <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[hsl(var(--dtk-focus))]" /> Candidati ora
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Invia il tuo curriculum a <a href={mailto} onClick={onApply} className="font-mono text-xs text-[hsl(var(--dtk-focus))] hover:underline" data-testid="job-apply-email-link">{pos.emailCandidature}</a>, includendo
            l&apos;autorizzazione al trattamento dei dati personali. Ricordati di allegare il CV.
          </p>
          <a
            href={mailto}
            onClick={onApply}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(214,58%,46%)] transition-colors"
            data-testid="job-apply-btn"
          >
            <Briefcase className="w-4 h-4" /> Candidati ora
          </a>
        </div>
      )}
    </div>
  );
}
